<template>
  <div class="flex flex-col">
    <div v-if="!parameterNames.length && !botOptions.length" class="text-sm text-gray">
      This tool takes no parameters, so there is nothing to fill in — just run it.
    </div>

    <div v-if="botOptions.length" class="flex flex-col w-64">
      <label class="text-sm font-medium text-gray-dark mb-2">Run as bot</label>
      <base-select v-model="botId" :options="botOptions" class="h-10"/>
      <span class="mt-2 text-xs text-gray">
        Decides which values the <code class="text-gray-dark">${{ '{' }}variable.*}</code> placeholders resolve to.
      </span>
    </div>

    <div v-for="name in parameterNames" :key="name" class="mt-4 flex flex-col">
      <label :for="`test-arg-${name}`" class="text-sm font-medium text-gray-dark mb-2">
        {{ name }}
        <span v-if="isRequired(name)" class="text-focus-error">*</span>
        <span class="ml-1 font-normal text-gray">{{ typeOf(name) }}</span>
      </label>
      <base-input :id="`test-arg-${name}`"
                  v-model="args[name]"
                  :data-testid="`test-arg-${name}`"
                  class="font-mono placeholder-gray"
                  :placeholder="descriptionOf(name)"
      />
    </div>

    <div class="mt-5 flex items-center gap-3">
      <busy-button id="tool-test-button"
                   data-testid="tool-test-run"
                   variant="primary"
                   :is-loading="isRunning"
                   :is-disabled="!canRun"
                   @click="run"
      >
        Send request
      </busy-button>
      <span v-if="missingRequired.length" class="text-sm text-gray">
        Fill in {{ missingRequired.join(', ') }} first.
      </span>
    </div>

    <div v-if="result" class="mt-5" data-testid="tool-test-result">
      <div class="flex items-baseline gap-3">
        <span class="px-2 py-1 rounded text-sm font-semibold" :class="statusClasses">
          {{ result.response.error ? 'Failed' : result.response.http_status }}
        </span>
        <span class="text-sm text-gray">{{ result.response.duration_ms }} ms</span>
      </div>

      <div class="mt-3 text-xs text-gray break-all">
        <span class="font-semibold text-gray-dark mr-1">{{ result.request.method }}</span>{{ result.request.url }}
      </div>

      <p v-if="result.response.message" class="mt-3 text-sm text-focus-error">{{ result.response.message }}</p>

      <pre class="mt-3 p-3 rounded bg-soft text-xs text-gray-dark overflow-auto tool-test-body">{{ prettyBody }}</pre>
    </div>
  </div>
</template>

<script>
import BusyButton from '@/components/common/BusyButton.vue'
import botModule from '@/store/modules/bot.module'
import toolsModule from '@/store/modules/tools.module'

export default {
  name: 'ToolTestPanel',

  components: { BusyButton },

  props: {
    // the SAVED tool — a test runs what is stored, not the unsaved draft
    tool: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      args: {},
      botId: null,
      isRunning: false,
      result: null
    }
  },

  computed: {
    botList: botModule.getBotList,

    parameterNames() {
      return Object.keys((this.tool.parameters && this.tool.parameters.properties) || {})
    },

    // Only worth asking which bot to run as when the request actually contains a ${variable.*} —
    // otherwise the answer cannot change anything.
    botOptions() {
      const usesVariables = JSON.stringify([
        this.tool.url, this.tool.body, this.tool.headers, this.tool.query_params
      ]).includes('${variable.')
      if (!usesVariables) return []

      return (this.botList || []).map(bot => ({ text: bot.name, value: bot.id }))
    },

    missingRequired() {
      const required = (this.tool.parameters && this.tool.parameters.required) || []
      return required.filter(name => !this.args[name])
    },

    canRun() {
      return !this.missingRequired.length
    },

    statusClasses() {
      if (this.result.response.error) return 'bg-focus-error-light text-focus-error'
      return this.result.response.http_status < 400 ?
        'bg-focus-success-light text-focus-success' :
        'bg-focus-error-light text-focus-error'
    },

    prettyBody() {
      const body = this.result.response.body
      if (body == null) return '(empty response)'
      return typeof body === 'string' ? body : JSON.stringify(body, null, 2)
    }
  },

  methods: {
    isRequired(name) {
      return ((this.tool.parameters && this.tool.parameters.required) || []).includes(name)
    },

    typeOf(name) {
      return this.tool.parameters.properties[name].type
    },

    descriptionOf(name) {
      return this.tool.parameters.properties[name].description || ''
    },

    async run() {
      this.isRunning = true
      // A previous result must not linger next to a new attempt that failed — it would read as the
      // response to the request just sent.
      this.result = null
      this.result = await toolsModule.testTool(this.tool.id, this.args, this.botId)
      this.isRunning = false
    }
  },

  mounted() {
    if (this.botOptions.length) this.botId = this.botOptions[0].value
  }
}
</script>

<style lang="scss" scoped>
.tool-test-body {
  max-height: 20rem;
  white-space: pre-wrap;
}
</style>
